import React, { useEffect, useMemo, useState } from 'react';
import { Link, useParams, useHistory } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faArrowLeft,
    faArrowUp,
    faCheck,
    faTimes,
    faShip,
    faFileInvoiceDollar,
    faHandshakeAngle,
    faTriangleExclamation,
    faRobot,
    faClockRotateLeft,
    faShieldHalved,
} from '@fortawesome/free-solid-svg-icons';
import Layout from '../Layout';
import axiosInstance from '../../utils/axiosConfig';
import { useFlashMessage } from '../FlashMessage';
import { getErrorMessage } from '../../utils/errorMessages';
import CRMCreditSnapshot from './CRMCreditSnapshot';
import { fetchPaymentTermOptions, formatPaymentTerms } from '../../services/paymentTermsService';
import { FINANCE_ONLY_KINDS, invoiceScreenPath } from '../../utils/invoiceScreenPath';
import './CreditApprovalScreen.css';

/**
 * Credit Approval Screen.
 *
 * Mounted at /dashboard/crm/credit-approvals/:requestId.
 *
 * Surfaces everything a Credit Approver needs to act on a single
 * CreditIncreaseRequest without bouncing between tabs:
 *   - Request panel:     who/what/why + Approve/Reject actions
 *   - Profile panel:     identity + payment terms (debtor/creditor)
 *   - Credit snapshot:   limit / allocations / available balance
 *   - Payment behaviour: the last five invoices the customer actually paid,
 *                        with the days late (minus = early), and the sanctions
 *                        light read from the KYC tab's last screening
 *                        (ClickUp 1248h532ugm, Chris's calls 2026-10-29)
 *   - Approve subject to: open invoices the approver ticks as having to be
 *                        paid first; stored on the request, shown on the deal,
 *                        cleared by itself when they are paid. Blocks nothing.
 *   - Invoices panel:    last 10 invoices for the partner
 *   - Deliveries panel:  open deliveries with vessel info
 *
 * The route is permissive — the backend approval-context endpoint enforces
 * "approver OR requester" and the approve/reject actions enforce
 * "approver only".
 */

const formatCurrency = (value, currency) => {
    if (value === null || value === undefined || value === '') return '-';
    const num = Number(value);
    if (!Number.isFinite(num)) return '-';
    const formatted = num.toLocaleString(undefined, {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
    });
    return currency ? `${currency} ${formatted}` : formatted;
};

const formatDate = (iso) => {
    if (!iso) return '-';
    try {
        return new Date(iso).toLocaleDateString(undefined, {
            year: 'numeric',
            month: 'short',
            day: '2-digit',
        });
    } catch {
        return iso;
    }
};

// Days late: the day the invoice was paid minus the day it was due. A minus
// means paid early (Marcel's "performance" figure).
const formatDaysLate = (days) => {
    if (days === null || days === undefined || days === '') return '-';
    const n = Number(days);
    if (!Number.isFinite(n)) return '-';
    if (n === 0) return '0 (on time)';
    return n > 0 ? `+${n} (late)` : `${n} (early)`;
};

const daysLateTone = (days) => {
    const n = Number(days);
    if (days === null || days === undefined || !Number.isFinite(n)) return undefined;
    if (n > 0) return 'late';
    if (n < 0) return 'early';
    return 'ontime';
};

const invoiceKey = (inv) => `${inv.kind || 'invoice'}:${inv.id}`;

/**
 * An invoice number on this screen. For a reader the server says may open
 * that kind of document it is a link to the document's own screen, opened
 * WITH its Invoice Papers beside it (ClickUp 2026-10-23, "double screen ...
 * where there are approvals"); everyone else sees the number as plain text,
 * because the screen would refuse them. The screen is chosen by the row's
 * kind, never by matching the type word; a kind with no screen stays plain
 * text. The notes' screens admit Financials only, so they take ``access.notes``.
 *
 * Opens in a NEW TAB: the approver is mid-decision here (ticks, review notes,
 * none of it saved yet), and this screen has no way back from an invoice. The
 * invoice with its papers beside it sits in the next tab, the decision in this one.
 */
const InvoiceNumber = ({ inv, access }) => {
    const label = inv.invoice_number || `#${inv.id}`;
    const allowed = FINANCE_ONLY_KINDS.includes(inv.kind) ? access.notes : access.invoices;
    const path = allowed ? invoiceScreenPath(inv.kind, inv.id, { withPapers: true }) : null;
    if (!path) return label;
    return (
        <Link
            to={path}
            target="_blank"
            rel="noopener noreferrer"
            className="credit-approval-invoice-link"
            title={`Open ${label} with its papers in a new tab`}
        >
            {label}
        </Link>
    );
};

// What a withheld or unreadable list says instead of "none" (row 147).
const LIST_NOTICE = {
    approvers_only: (what) => `The partner's ${what} are shown to credit approvers only.`,
    unavailable: (what) =>
        `The partner's ${what} could not be loaded just now, so none are shown. Refresh the page to try again.`,
};

const STATUS_BADGE = {
    pending: { label: 'PENDING REVIEW', cls: 'pending' },
    approved: { label: 'APPROVED', cls: 'approved' },
    rejected: { label: 'REJECTED', cls: 'rejected' },
};

const CreditApprovalScreen = () => {
    const { requestId } = useParams();
    const history = useHistory();
    const { showError, showSuccess } = useFlashMessage();

    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [context, setContext] = useState(null);
    const [reviewNotes, setReviewNotes] = useState('');
    const [submitting, setSubmitting] = useState(false);
    const [confirmAction, setConfirmAction] = useState(null);
    // Terms are stored as codes ('NET45'), so the labels have to be looked up.
    // A failed load is not fatal here: formatPaymentTerms falls back to the
    // stored value, so the reviewer still sees something real.
    const [paymentTermOptions, setPaymentTermOptions] = useState([]);
    // "Approve subject to": the open invoices the approver has ticked, as kind:id keys.
    const [paidFirst, setPaidFirst] = useState([]);
    const [screening, setScreening] = useState(false);

    // ``quiet`` re-reads the page in place (after Screen now) without the loading
    // state replacing everything on screen; a quiet failure is told as a flash.
    const loadContext = async ({ quiet = false } = {}) => {
        if (!quiet) {
            setLoading(true);
            setError(null);
        }
        try {
            const resp = await axiosInstance.get(
                `/api/crm/credit-requests/${requestId}/approval-context/`
            );
            setContext(resp.data || null);
        } catch (err) {
            const message =
                err?.response?.data?.error ||
                err?.response?.data?.detail ||
                getErrorMessage(err) ||
                'Failed to load credit request.';
            if (quiet) showError(message);
            else setError(message);
        } finally {
            if (!quiet) setLoading(false);
        }
    };

    useEffect(() => {
        let cancelled = false;
        fetchPaymentTermOptions()
            .then((options) => { if (!cancelled) setPaymentTermOptions(options); })
            .catch(() => { if (!cancelled) setPaymentTermOptions([]); });
        return () => { cancelled = true; };
    }, []);

    useEffect(() => {
        loadContext();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [requestId]);

    const request = context?.request || null;
    const profile = context?.profile || null;
    const recentInvoices = context?.recent_invoices || [];
    const activeDeliveries = context?.active_deliveries || [];
    // Why each list holds what it holds (ledger row 147): 'shown',
    // 'approvers_only' (the partner's invoices and deliveries are the credit
    // approver's evidence; the person who filed sees their request) or
    // 'unavailable' (the list could not be read). An empty list must never be
    // read as "this partner has none" unless it really was shown.
    const invoicesState = context?.lists?.recent_invoices || 'shown';
    const deliveriesState = context?.lists?.active_deliveries || 'shown';
    // The last five invoices the customer paid, and the open ones an approval
    // may be made subject to: invoices too, so the same three states apply.
    const paymentBehaviour = context?.payment_behaviour || [];
    const openInvoices = context?.open_invoices || [];
    const behaviourState = context?.lists?.payment_behaviour || 'shown';
    const openState = context?.lists?.open_invoices || 'shown';
    // The sanctions light: green, red, grey (never screened) or unknown (unreadable).
    const sanctions = context?.sanctions || null;
    // The decided request's condition, read live: each invoice paid or not, met, words.
    const conditions = request?.conditions || null;
    const tickedInvoices = openInvoices.filter((inv) => paidFirst.includes(invoiceKey(inv)));
    const tickedNumbers = tickedInvoices.map((inv) => inv.invoice_number || `#${inv.id}`);
    const toggleTick = (inv) => {
        const key = invoiceKey(inv);
        setPaidFirst((prev) => (prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key]));
    };

    const status = request?.status || 'pending';
    const statusBadge = STATUS_BADGE[status] || STATUS_BADGE.pending;
    const isPending = status === 'pending';
    // Only a credit approver may approve or reject (the server refuses anyone
    // else), so only they are offered the buttons. An older server sends no
    // answer; the buttons then show as before.
    const canDecide = context?.can_decide !== false;
    // May this person open an invoice's own screen, and a note's? The server
    // answers with the invoice doors' own gate (the notes' screens take
    // Financials only). No answer (an older server) means no links: a number
    // that opens a screen which then refuses them is worse than text.
    const invoiceAccess = {
        invoices: context?.can_open_invoices === true,
        notes: context?.can_open_notes === true,
    };

    const requestedAmountNum = Number(request?.requested_amount);
    const safeRequested = Number.isFinite(requestedAmountNum)
        ? requestedAmountNum
        : null;

    const decisionMade = useMemo(() => {
        if (!request) return null;
        if (request.status === 'approved') {
            return {
                label: 'Approved',
                by: request.reviewed_by_name,
                at: request.reviewed_at,
                notes: request.review_notes,
            };
        }
        if (request.status === 'rejected') {
            return {
                label: 'Rejected',
                by: request.reviewed_by_name,
                at: request.reviewed_at,
                notes: request.review_notes,
            };
        }
        return null;
    }, [request]);

    const handleAction = async (action) => {
        if (!isPending) return;
        if (action === 'reject' && !reviewNotes.trim()) {
            showError('Please add a note explaining why the request is rejected.');
            return;
        }
        setSubmitting(true);
        try {
            const body = { review_notes: reviewNotes.trim() };
            if (action === 'approve') {
                // The ticked open invoices: the server stores them on the request
                // and refuses any that are not this partner's own open invoices.
                body.paid_first_invoices = tickedInvoices.map(({ kind, id }) => ({ kind, id }));
            }
            const resp = await axiosInstance.post(
                `/api/crm/credit-requests/${requestId}/${action}/`,
                body
            );
            const updated = resp.data || {};
            setContext((prev) => (prev ? { ...prev, request: updated } : prev));
            const conditionWords = updated?.conditions?.text
                ? ` Approved ${updated.conditions.text}.`
                : '';
            showSuccess(
                action === 'approve'
                    ? `Credit request approved. ${
                          safeRequested !== null
                              ? `Limit increased by ${formatCurrency(safeRequested)}.`
                              : ''
                      }${conditionWords}`.trim()
                    : 'Credit request rejected.'
            );
            setConfirmAction(null);
        } catch (err) {
            const message =
                err?.response?.data?.error ||
                err?.response?.data?.detail ||
                getErrorMessage(err) ||
                `Failed to ${action} credit request.`;
            showError(message);
        } finally {
            setSubmitting(false);
        }
    };

    // "Screen now" runs the existing SECO list screening for this partner (the
    // same door as the KYC tab's button), then re-reads the light in place.
    const handleScreenNow = async () => {
        if (!profile?.id || screening) return;
        setScreening(true);
        try {
            await axiosInstance.post(`/api/crm/profiles/${profile.id}/seco-screening/screen/`);
            showSuccess('Sanctions screening complete.');
            await loadContext({ quiet: true });
        } catch (err) {
            showError(
                err?.response?.data?.error ||
                err?.response?.data?.detail ||
                getErrorMessage(err) ||
                'The sanctions screening could not be run. Please try again.'
            );
        } finally {
            setScreening(false);
        }
    };

    if (loading) {
        return (
            <Layout showLeftNavSidebar={true} showRightSidebar={false}>
                <div className="credit-approval-screen credit-approval-screen--loading">
                    <div className="credit-approval-spinner" />
                    <p>Loading credit request...</p>
                </div>
            </Layout>
        );
    }

    if (error || !request || !profile) {
        return (
            <Layout showLeftNavSidebar={true} showRightSidebar={false}>
                <div className="credit-approval-screen credit-approval-screen--error">
                    <FontAwesomeIcon icon={faTriangleExclamation} />
                    <p>{error || 'Credit request not found.'}</p>
                    <button
                        type="button"
                        className="credit-approval-btn credit-approval-btn--secondary"
                        onClick={() => history.push('/dashboard/notifications')}
                    >
                        Back to Notifications
                    </button>
                </div>
            </Layout>
        );
    }

    return (
        <Layout showLeftNavSidebar={true} showRightSidebar={false}>
            <div className="credit-approval-screen">
                <div className="credit-approval-header">
                    <button
                        type="button"
                        className="credit-approval-back"
                        onClick={() => history.goBack()}
                    >
                        <FontAwesomeIcon icon={faArrowLeft} />
                        Back
                    </button>
                    <div className="credit-approval-title-block">
                        <h1 className="credit-approval-title">
                            Credit Increase Request
                        </h1>
                        <span className="credit-approval-subtitle">
                            {profile.legal_entity_name}
                            {profile.trading_short_name
                                ? ` (${profile.trading_short_name})`
                                : ''}
                        </span>
                    </div>
                    <span
                        className={`credit-approval-status credit-approval-status--${statusBadge.cls}`}
                    >
                        {statusBadge.label}
                    </span>
                </div>

                {request.is_auto_flagged && (
                    <div className="credit-approval-banner">
                        <FontAwesomeIcon icon={faRobot} />
                        <div>
                            <strong>System-flagged request.</strong>{' '}
                            This request was automatically created because the partner's
                            credit balance went negative. Review the snapshot and decide
                            whether to extend the limit.
                        </div>
                    </div>
                )}

                <div className="credit-approval-grid">
                    {/* PANEL 1 - Request details + Actions */}
                    <section className="credit-approval-panel credit-approval-panel--request">
                        <header className="credit-approval-panel-header">
                            <FontAwesomeIcon icon={faHandshakeAngle} />
                            <h2>Request</h2>
                        </header>
                        <div className="credit-approval-request-grid">
                            <div className="credit-approval-kv">
                                <span className="credit-approval-kv-label">
                                    Requested Increase
                                </span>
                                <span className="credit-approval-kv-value credit-approval-kv-value--big credit-approval-kv-value--increase">
                                    <span className="credit-approval-increase-badge" aria-hidden="true">
                                        <FontAwesomeIcon icon={faArrowUp} />
                                    </span>
                                    {safeRequested !== null
                                        ? formatCurrency(safeRequested)
                                        : '-'}
                                </span>
                                {safeRequested !== null && (
                                    <span className="credit-approval-kv-hint">
                                        Lifts limit from{' '}
                                        <strong>
                                            {formatCurrency(profile.credit_limit)}
                                        </strong>{' '}
                                        to{' '}
                                        <strong>
                                            {formatCurrency(
                                                Number(profile.credit_limit || 0) +
                                                    safeRequested
                                            )}
                                        </strong>
                                    </span>
                                )}
                            </div>
                            <div className="credit-approval-kv">
                                <span className="credit-approval-kv-label">
                                    Current Limit
                                </span>
                                <span className="credit-approval-kv-value">
                                    {formatCurrency(profile.credit_limit)}
                                </span>
                            </div>
                            <div className="credit-approval-kv">
                                <span className="credit-approval-kv-label">
                                    Projected Limit
                                </span>
                                <span className="credit-approval-kv-value">
                                    {safeRequested !== null
                                        ? formatCurrency(
                                              Number(profile.credit_limit || 0) +
                                                  safeRequested
                                          )
                                        : '-'}
                                </span>
                            </div>
                            <div className="credit-approval-kv">
                                <span className="credit-approval-kv-label">
                                    Available Balance
                                </span>
                                <span
                                    className="credit-approval-kv-value"
                                    data-negative={
                                        Number(profile.credit_balance) < 0
                                            ? 'true'
                                            : 'false'
                                    }
                                >
                                    {formatCurrency(profile.credit_balance)}
                                </span>
                            </div>
                            <div className="credit-approval-kv">
                                <span className="credit-approval-kv-label">
                                    Requested By
                                </span>
                                <span className="credit-approval-kv-value">
                                    {request.requested_by_name || 'System'}
                                </span>
                            </div>
                            <div className="credit-approval-kv">
                                <span className="credit-approval-kv-label">
                                    Submitted
                                </span>
                                <span className="credit-approval-kv-value">
                                    {formatDate(request.created_at)}
                                </span>
                            </div>
                            {request.deal_reference && (
                                <div className="credit-approval-kv credit-approval-kv--full">
                                    <span className="credit-approval-kv-label">
                                        Deal Reference
                                    </span>
                                    <span className="credit-approval-kv-value">
                                        {request.deal_reference}
                                    </span>
                                </div>
                            )}
                            <div className="credit-approval-kv credit-approval-kv--full">
                                <span className="credit-approval-kv-label">Reason</span>
                                <span className="credit-approval-kv-value credit-approval-kv-value--multiline">
                                    {request.reason || '-'}
                                </span>
                            </div>
                        </div>

                        {decisionMade && (
                            <div
                                className={`credit-approval-decision credit-approval-decision--${status}`}
                            >
                                <strong>{decisionMade.label}</strong> by{' '}
                                {decisionMade.by || 'unknown'} on{' '}
                                {formatDate(decisionMade.at)}.
                                {decisionMade.notes && (
                                    <p className="credit-approval-decision-notes">
                                        {decisionMade.notes}
                                    </p>
                                )}
                                {conditions && (
                                    <div
                                        className="credit-approval-conditions-state"
                                        data-met={conditions.met ? 'true' : 'false'}
                                    >
                                        <strong>
                                            {conditions.met
                                                ? 'Condition met.'
                                                : `Approved ${conditions.text}.`}
                                        </strong>
                                        <ul>
                                            {conditions.invoices.map((inv) => (
                                                <li key={invoiceKey(inv)}>
                                                    {inv.invoice_number || `#${inv.id}`}:{' '}
                                                    {inv.paid
                                                        ? `paid ${formatDate(inv.paid_on)}`
                                                        : inv.released
                                                        ? inv.note
                                                        : 'not yet paid'}
                                                    {!inv.released && inv.note ? ` (${inv.note})` : ''}
                                                </li>
                                            ))}
                                        </ul>
                                    </div>
                                )}
                            </div>
                        )}

                        {isPending && !canDecide && (
                            <p className="credit-approval-empty">
                                Only a credit approver can approve or reject this request.
                            </p>
                        )}

                        {isPending && canDecide && (
                            <div className="credit-approval-actions">
                                <fieldset className="credit-approval-conditions">
                                    <legend>
                                        Approve subject to invoices being paid first{' '}
                                        <span>(optional: tick any that must be paid; nothing is blocked)</span>
                                    </legend>
                                    {openState !== 'shown' ? (
                                        <p className="credit-approval-empty">
                                            {(LIST_NOTICE[openState] || LIST_NOTICE.unavailable)('open invoices')}
                                        </p>
                                    ) : openInvoices.length === 0 ? (
                                        <p className="credit-approval-empty">
                                            This partner has no open invoices, so the approval carries no payment condition.
                                        </p>
                                    ) : (
                                        <ul className="credit-approval-tick-list">
                                            {openInvoices.map((inv) => (
                                                <li key={invoiceKey(inv)}>
                                                    <label>
                                                        <input
                                                            type="checkbox"
                                                            checked={paidFirst.includes(invoiceKey(inv))}
                                                            onChange={() => toggleTick(inv)}
                                                            disabled={submitting}
                                                        />
                                                        <span className="credit-approval-tick-number">
                                                            <InvoiceNumber inv={inv} access={invoiceAccess} />
                                                        </span>
                                                        <span>
                                                            {formatCurrency(inv.open_amount ?? inv.gross_amount, inv.currency)} open
                                                            {' · due '}
                                                            {formatDate(inv.due_date)}
                                                        </span>
                                                    </label>
                                                </li>
                                            ))}
                                        </ul>
                                    )}
                                </fieldset>
                                <label className="credit-approval-notes-label">
                                    Review notes
                                    <textarea
                                        className="credit-approval-notes"
                                        value={reviewNotes}
                                        onChange={(e) => setReviewNotes(e.target.value)}
                                        placeholder="Required when rejecting; optional when approving"
                                        disabled={submitting}
                                        rows={3}
                                    />
                                </label>
                                <div className="credit-approval-action-buttons">
                                    <button
                                        type="button"
                                        className="credit-approval-btn credit-approval-btn--reject"
                                        onClick={() => setConfirmAction('reject')}
                                        disabled={submitting}
                                    >
                                        <FontAwesomeIcon icon={faTimes} />
                                        Reject
                                    </button>
                                    <button
                                        type="button"
                                        className="credit-approval-btn credit-approval-btn--approve"
                                        onClick={() => setConfirmAction('approve')}
                                        disabled={submitting || safeRequested === null}
                                    >
                                        <FontAwesomeIcon icon={faCheck} />
                                        Approve
                                    </button>
                                </div>
                            </div>
                        )}
                    </section>

                    {/* PANEL 2 - Profile + Payment Terms */}
                    <section className="credit-approval-panel credit-approval-panel--profile">
                        <header className="credit-approval-panel-header">
                            <h2>Partner &amp; Payment Terms</h2>
                        </header>
                        <div className="credit-approval-kv-grid">
                            <div className="credit-approval-kv">
                                <span className="credit-approval-kv-label">
                                    Legal Entity
                                </span>
                                <span className="credit-approval-kv-value">
                                    {profile.legal_entity_name || '-'}
                                </span>
                            </div>
                            <div className="credit-approval-kv">
                                <span className="credit-approval-kv-label">
                                    Trading Name
                                </span>
                                <span className="credit-approval-kv-value">
                                    {profile.trading_short_name || '-'}
                                </span>
                            </div>
                            <div className="credit-approval-kv">
                                <span className="credit-approval-kv-label">
                                    Approval Status
                                </span>
                                <span
                                    className="credit-approval-kv-value"
                                    data-status={profile.approval_status}
                                >
                                    {(profile.approval_status || 'pending').toUpperCase()}
                                </span>
                            </div>
                            <div className="credit-approval-kv">
                                <span className="credit-approval-kv-label">
                                    Partner Credit Days
                                </span>
                                <span className="credit-approval-kv-value">
                                    {profile.partner_credit_days ?? '-'}
                                </span>
                            </div>
                            <div className="credit-approval-kv credit-approval-kv--full">
                                <span className="credit-approval-kv-label">
                                    Payment Terms (as Debtor)
                                </span>
                                <span className="credit-approval-kv-value credit-approval-kv-value--multiline">
                                    {formatPaymentTerms(profile.payment_terms_as_debtor, paymentTermOptions)}
                                </span>
                            </div>
                            <div className="credit-approval-kv credit-approval-kv--full">
                                <span className="credit-approval-kv-label">
                                    Payment Terms (as Creditor)
                                </span>
                                <span className="credit-approval-kv-value credit-approval-kv-value--multiline">
                                    {formatPaymentTerms(profile.payment_terms_as_creditor, paymentTermOptions)}
                                </span>
                            </div>
                        </div>

                        <div className="credit-approval-snapshot-wrap">
                            <CRMCreditSnapshot
                                profile={profile}
                                requestedIncrease={
                                    isPending && safeRequested !== null
                                        ? safeRequested
                                        : null
                                }
                            />
                        </div>
                    </section>

                    {/* PANEL 2b - Payment behaviour: the last five invoices the customer paid */}
                    <section className="credit-approval-panel credit-approval-panel--behaviour">
                        <header className="credit-approval-panel-header">
                            <FontAwesomeIcon icon={faClockRotateLeft} />
                            <h2>Payment behaviour</h2>
                            <span className="credit-approval-panel-count">
                                {behaviourState === 'shown' ? paymentBehaviour.length : '—'}
                            </span>
                        </header>
                        <p className="credit-approval-panel-help">
                            The last five invoices this customer paid. Days late is the day paid
                            minus the day due; a minus means paid early.
                        </p>
                        {behaviourState !== 'shown' ? (
                            <p className="credit-approval-empty">
                                {(LIST_NOTICE[behaviourState] || LIST_NOTICE.unavailable)('paid invoices')}
                            </p>
                        ) : paymentBehaviour.length === 0 ? (
                            <p className="credit-approval-empty">
                                New customer, no payment history yet.
                            </p>
                        ) : (
                            <>
                                {paymentBehaviour.length < 5 && (
                                    <p className="credit-approval-note">
                                        New customer: only {paymentBehaviour.length} paid{' '}
                                        {paymentBehaviour.length === 1 ? 'invoice' : 'invoices'} so far.
                                    </p>
                                )}
                                <table className="credit-approval-table">
                                    <thead>
                                        <tr>
                                            <th>Invoice #</th>
                                            <th>Amount</th>
                                            <th>Due</th>
                                            <th>Paid</th>
                                            <th>Days late</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {paymentBehaviour.map((inv) => (
                                            <tr key={invoiceKey(inv)}>
                                                <td><InvoiceNumber inv={inv} access={invoiceAccess} /></td>
                                                <td>{formatCurrency(inv.gross_amount, inv.currency)}</td>
                                                <td>{formatDate(inv.due_date)}</td>
                                                <td>{formatDate(inv.paid_on)}</td>
                                                <td>
                                                    <span
                                                        className="credit-approval-days"
                                                        data-late={daysLateTone(inv.days_late)}
                                                    >
                                                        {formatDaysLate(inv.days_late)}
                                                    </span>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </>
                        )}
                    </section>

                    {/* PANEL 2c - Sanctions light, from the KYC tab's last screening */}
                    <section className="credit-approval-panel credit-approval-panel--sanctions">
                        <header className="credit-approval-panel-header">
                            <FontAwesomeIcon icon={faShieldHalved} />
                            <h2>Sanctions screening</h2>
                        </header>
                        {sanctions ? (
                            <>
                                <div className="credit-approval-light-row">
                                    <span
                                        className={`credit-approval-light credit-approval-light--${sanctions.light}`}
                                        role="img"
                                        aria-label={`Sanctions light: ${sanctions.light}`}
                                        data-testid="sanctions-light"
                                    />
                                    <div className="credit-approval-light-text">
                                        <strong>{sanctions.label}</strong>
                                        <span>{sanctions.detail}</span>
                                        {sanctions.screened_at && (
                                            <span className="credit-approval-light-meta">
                                                Last screened {formatDate(sanctions.screened_at)}
                                                {sanctions.source ? ` · ${sanctions.source}` : ''}
                                            </span>
                                        )}
                                    </div>
                                </div>
                                <div className="credit-approval-light-actions">
                                    {sanctions.light === 'grey' && (
                                        <button
                                            type="button"
                                            className="credit-approval-btn credit-approval-btn--secondary"
                                            onClick={handleScreenNow}
                                            disabled={screening || !sanctions.can_screen}
                                        >
                                            {screening ? 'Screening…' : 'Screen now'}
                                        </button>
                                    )}
                                    {context?.kyc_tab_path && (
                                        <button
                                            type="button"
                                            className="credit-approval-link"
                                            onClick={() => history.push(context.kyc_tab_path)}
                                        >
                                            Open the partner&apos;s KYC &amp; Compliance tab
                                        </button>
                                    )}
                                </div>
                            </>
                        ) : (
                            <p className="credit-approval-empty">
                                The sanctions screening could not be read just now. Refresh the page to try again.
                            </p>
                        )}
                    </section>

                    {/* PANEL 3 - Recent Invoices */}
                    <section className="credit-approval-panel">
                        <header className="credit-approval-panel-header">
                            <FontAwesomeIcon icon={faFileInvoiceDollar} />
                            <h2>Recent Invoices</h2>
                            <span className="credit-approval-panel-count">
                                {invoicesState === 'shown' ? recentInvoices.length : '—'}
                            </span>
                        </header>
                        {invoicesState !== 'shown' ? (
                            <p className="credit-approval-empty">
                                {(LIST_NOTICE[invoicesState] || LIST_NOTICE.unavailable)('invoices')}
                            </p>
                        ) : recentInvoices.length === 0 ? (
                            <p className="credit-approval-empty">
                                No invoices found for this partner.
                            </p>
                        ) : (
                            <table className="credit-approval-table">
                                <thead>
                                    <tr>
                                        <th>Invoice #</th>
                                        <th>Type</th>
                                        <th>Date</th>
                                        <th>Due</th>
                                        <th>Net</th>
                                        <th>Status</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {recentInvoices.map((inv) => (
                                        <tr key={`${inv.document_type || 'invoice'}-${inv.id}`}>
                                            <td><InvoiceNumber inv={inv} access={invoiceAccess} /></td>
                                            <td>{inv.document_type || '-'}</td>
                                            <td>{formatDate(inv.invoice_date)}</td>
                                            <td>{formatDate(inv.due_date)}</td>
                                            <td>
                                                {formatCurrency(
                                                    inv.net_amount,
                                                    inv.currency
                                                )}
                                            </td>
                                            <td>
                                                <span
                                                    className="credit-approval-pill"
                                                    data-status={inv.status}
                                                >
                                                    {(inv.status || '-').toUpperCase()}
                                                </span>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        )}
                    </section>

                    {/* PANEL 4 - Active Deliveries / Vessels */}
                    <section className="credit-approval-panel">
                        <header className="credit-approval-panel-header">
                            <FontAwesomeIcon icon={faShip} />
                            <h2>Active Deliveries</h2>
                            <span className="credit-approval-panel-count">
                                {deliveriesState === 'shown' ? activeDeliveries.length : '—'}
                            </span>
                        </header>
                        {deliveriesState !== 'shown' ? (
                            <p className="credit-approval-empty">
                                {(LIST_NOTICE[deliveriesState] || LIST_NOTICE.unavailable)('deliveries')}
                            </p>
                        ) : activeDeliveries.length === 0 ? (
                            <p className="credit-approval-empty">
                                No active deliveries linked to this partner.
                            </p>
                        ) : (
                            <table className="credit-approval-table">
                                <thead>
                                    <tr>
                                        <th>Vessel</th>
                                        <th>IMO</th>
                                        <th>ETA</th>
                                        <th>Loading Port</th>
                                        <th>Delivery Port</th>
                                        <th>Side</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {activeDeliveries.map((d) => (
                                        <tr key={d.id}>
                                            <td>{d.vessel_name || '-'}</td>
                                            <td>{d.vessel_imo_number || '-'}</td>
                                            <td>{formatDate(d.eta)}</td>
                                            <td>{d.loading_port_location || '-'}</td>
                                            <td>{d.discharging_port_location || '-'}</td>
                                            <td>
                                                <span
                                                    className="credit-approval-pill"
                                                    data-status={d.side}
                                                >
                                                    {(d.side || '-').toUpperCase()}
                                                </span>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        )}
                    </section>
                </div>

                {confirmAction && (
                    <div
                        className="credit-approval-modal-overlay"
                        role="dialog"
                        aria-modal="true"
                    >
                        <div className="credit-approval-modal">
                            <h3>
                                {confirmAction === 'approve'
                                    ? 'Approve credit increase?'
                                    : 'Reject credit increase?'}
                            </h3>
                            <p>
                                {confirmAction === 'approve'
                                    ? `This will increase ${
                                          profile.legal_entity_name
                                      }'s credit limit by ${formatCurrency(
                                          safeRequested
                                      )}. The requester will be notified.${
                                          tickedNumbers.length
                                              ? ` Approved subject to ${tickedNumbers.join(', ')} being paid first; the deal will show this until ${
                                                    tickedNumbers.length === 1 ? 'it is' : 'they are'
                                                } paid.`
                                              : ''
                                      }`
                                    : `This will reject the request and notify the requester. They will need to submit a new request to try again.`}
                            </p>
                            <div className="credit-approval-modal-buttons">
                                <button
                                    type="button"
                                    className="credit-approval-btn credit-approval-btn--secondary"
                                    onClick={() => setConfirmAction(null)}
                                    disabled={submitting}
                                >
                                    Cancel
                                </button>
                                <button
                                    type="button"
                                    className={`credit-approval-btn credit-approval-btn--${
                                        confirmAction === 'approve' ? 'approve' : 'reject'
                                    }`}
                                    onClick={() => handleAction(confirmAction)}
                                    disabled={submitting}
                                >
                                    {submitting
                                        ? 'Submitting...'
                                        : confirmAction === 'approve'
                                        ? 'Confirm Approval'
                                        : 'Confirm Rejection'}
                                </button>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </Layout>
    );
};

export default CreditApprovalScreen;
